import * as Sentry from '@sentry/browser';
import { useFlags } from 'launchdarkly-react-client-sdk';
import get from 'lodash/get';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Redirect, RouteComponentProps, useLocation } from 'react-router-dom';
import { useAnalytics } from '@modernhealth/web-analytics';
import { EventLexicon } from '@modernhealth/analytics-lexicon';

import { appClient } from 'src/config/apollo';
import { config } from 'src/config/config';
import { toastNotificationsService } from 'src/config/rootService';
import { CareOption, CareRecommendationState } from 'src/generated/webapp_gql';
import { useAcceptCareRecommendationMutation } from 'src/modules/onboarding/documents/acceptCareRecommendationMutation.generated';
import { ProviderType } from 'src/modules/user/models/user';
import type { ScheduleSessionCalendarPageLocationState } from 'src/modules/videoSessions/pages/ScheduleSessionCalendarPage';
import {
    getAdditionalInfoProvided,
    getMostRecentCareRecommendation,
} from 'src/modules/user/selectors/userSelectors';
import { getSessionUtilizationInfo } from 'src/modules/videoSessions/selectors/videoSessionsSelectors';

import { AdditionalInfo } from '../components/AdditionalInfo';
import { ProviderSelection } from '../components/ProviderSelection';
import { WorkWithProviderIntro } from '../components/WorkWithProviderIntro';
import { useProvider } from '../hooks/useProvider';

enum Step {
    INTRO = 'intro',
    ADDITIONAL_INFO = 'additionalInfo',
    CHOOSE = 'choose',
}

const WorkWithCoachPage: React.FC<RouteComponentProps> = ({ history }) => {
    const location = useLocation<{ coachToRemoveUuid?: string; onboarding?: boolean }>();
    const coachToRemoveUuid = location?.state?.coachToRemoveUuid;
    const analytics = useAnalytics();
    const { t } = useTranslation();
    const {
        fetchProviders,
        removeProvider,
        addProvider,
        fetchMyProviders,
        matchedPreferenceProviders,
        notMatchedPreferenceProviders,
        isMatchedProvidersLoading,
        allowsFiltering,
        myCoaches,
        matchedAttempt,
    } = useProvider({ providerType: CareOption.Coaching });

    const [acceptCareRecommendation] = useAcceptCareRecommendationMutation({
        client: appClient,
    });
    const careRecommendation = useSelector(getMostRecentCareRecommendation);

    const flags = useFlags();

    const [step, setStep] = useState(coachToRemoveUuid ? Step.CHOOSE : Step.INTRO);
    const [selectedCoach, setSelectedCoach] = useState('');

    const additionalInfoProvided = useSelector(getAdditionalInfoProvided);
    const { coveredCoachingVisits, coachingVisitsRemaining } =
        useSelector(getSessionUtilizationInfo);

    const numberOfCoachesPerMember = 2;
    const myCoachesLimitReached = myCoaches.length >= numberOfCoachesPerMember;

    useEffect(() => {
        if (myCoachesLimitReached && step === Step.INTRO && !coachToRemoveUuid) {
            toastNotificationsService.renderErrorToast(
                t('providers:coach.notification.max_coaches'),
            );
            history.replace('/');
        }
    }, [myCoaches, myCoachesLimitReached, step, coachToRemoveUuid, t, history]);

    // do not show intro step again when user decided
    // to go back to coach selection from session scheduling
    useEffect(() => {
        if (step === Step.INTRO && history.action === 'POP') {
            setStep(Step.CHOOSE);
        }
    }, [step, history]);

    useEffect(() => {
        if (careRecommendation?.uuid && step === Step.CHOOSE) {
            fetchProviders(careRecommendation.uuid, []);
        }
    }, [step]);

    useEffect(() => {
        navigateToCalendar(selectedCoach);
    }, [selectedCoach]);

    const navigateToCalendar = async (coachUuid: string) => {
        if (!coachUuid) {
            return;
        }

        if (coachToRemoveUuid) {
            await removeProvider(coachToRemoveUuid);
        }

        const success = await addProvider(coachUuid, matchedAttempt);

        if (!success) {
            if (careRecommendation?.uuid) {
                await fetchProviders(careRecommendation?.uuid, []);
            }

            setSelectedCoach('');
        }

        await fetchMyProviders();
        history.push('/schedule-session/calendar', {
            coachUuid,
            isFirstSession: coveredCoachingVisits === coachingVisitsRemaining,
            removeCoachAssignmentOnBackPress: coachToRemoveUuid ? false : true,
            disableBack: coachToRemoveUuid ? true : false,
            onboarding: location.state?.onboarding || false,
            providerMatchAttemptUuid: matchedAttempt,
        } as ScheduleSessionCalendarPageLocationState);
    };

    const handleCoachPress = async (coachUuid: string) => {
        if (selectedCoach) {
            return;
        }

        analytics.trackEvent(
            EventLexicon.clickProvidersMatchWithAProvider({
                providerType: ProviderType.COACH.toLowerCase(),
                providerUuid: coachUuid,
                providerMatchAttemptUuid: matchedAttempt,
            }),
        );
        setSelectedCoach(coachUuid);
    };

    const handleContinue = async () => {
        analytics.trackEvent(
            EventLexicon.clickProvidersChooseYourProviderIntroScreen({
                action: 'continue',
                providerType: ProviderType.COACH.toLowerCase(),
            }),
        );

        try {
            // for users that have not gone through the new onboarding so dont have any care recs
            if (!careRecommendation?.uuid) {
                setStep(additionalInfoProvided ? Step.CHOOSE : Step.ADDITIONAL_INFO);
                return;
            }

            const res = await acceptCareRecommendation({
                variables: {
                    uuid: careRecommendation?.uuid,
                    outcome: CareOption.Coaching,
                },
            });
            const careUuid = get(res, 'data.acceptCareRecommendation.careRecommendation.uuid');
            if (!careUuid) {
                throw new Error('Failed to accept care recommendation');
            }

            setStep(additionalInfoProvided ? Step.CHOOSE : Step.ADDITIONAL_INFO);
        } catch (error) {
            setStep(additionalInfoProvided ? Step.CHOOSE : Step.ADDITIONAL_INFO);

            toastNotificationsService.renderErrorToast(config.messages.genericError);

            Sentry.withScope(scope => {
                scope.setExtra('error_info', 'Error: failed to accept a care recommendation');

                Sentry.captureException(error);
            });
        }
    };

    // onboarding shortcut experiment -- user has to finish onboarding first
    if (careRecommendation?.state === CareRecommendationState.BypassedAssessments) {
        return (
            <Redirect
                to={{
                    pathname: '/a-few-questions',
                    state: { matchAttempt: true },
                }}
            />
        );
    }

    if (myCoachesLimitReached && !coachToRemoveUuid) {
        return null;
    }

    if (step === Step.INTRO) {
        return (
            <WorkWithProviderIntro
                providerType={ProviderType.COACH}
                onChooseProviderClick={handleContinue}
                onNotNowClick={() => {
                    analytics.trackEvent(
                        EventLexicon.clickProvidersChooseYourProviderIntroScreen({
                            action: 'cancel',
                            providerType: ProviderType.COACH.toLowerCase(),
                        }),
                    );
                    history.push('/dashboard');
                }}
            />
        );
    }

    if (step === Step.ADDITIONAL_INFO) {
        return <AdditionalInfo onSubmit={() => setStep(Step.CHOOSE)} />;
    }

    const showPreferenceFilters = !!(flags?.showDemographicsProjectWeb && allowsFiltering);

    if (step === Step.CHOOSE) {
        return (
            <ProviderSelection
                providers={[]}
                onProviderPress={handleCoachPress}
                selectedProvider={selectedCoach}
                loadingMatches={isMatchedProvidersLoading}
                showMatchedProviders
                showFilters={showPreferenceFilters}
                matchedProviders={matchedPreferenceProviders}
                notMatchedProviders={notMatchedPreferenceProviders}
                refetchMatchedProviders={races =>
                    careRecommendation?.uuid && fetchProviders(careRecommendation.uuid, races)
                }
                onBackPress={() => {
                    analytics.trackEvent(
                        EventLexicon.clickBack({
                            destination: Step.ADDITIONAL_INFO as string,
                        }),
                    );
                    setStep(Step.ADDITIONAL_INFO);
                }}
                showBackButton={location?.state?.onboarding}
                headerText={t('providers:provider_selection.title.not_matched.coach')}
                subTitleText={t('providers:provider_selection.description.virtual')}
            />
        );
    }

    return null;
};

export default WorkWithCoachPage;
